<script lang="ts">
    import * as Card from '@/components/ui/card';
    import { Badge } from '@/components/ui/badge';
    import { Button } from '@/components/ui/button';
    import PicklistBadge from '@/components/ui/picklist-badge/PicklistBadge.svelte';
    import { Calendar, Users, Pencil, Trash2, Car, CalendarDays } from 'lucide-svelte';
    import { useForm, router } from '@inertiajs/svelte';
    import { toast } from 'svelte-sonner';

    type SiteVisit = {
        id: string;
        title: string;
        purpose: string | null;
        status: string;
        start_date_time: string;
        end_date_time: string;
        notes: string | null;
        findings: string | null;
        employee_assignments?: any[];
        visitable_type: string;
        visitable_id: string;
        vehicle?: {
            id: string;
            make: string;
            model: string;
            registration: string;
        } | null;
    };

    type Props = {
        siteVisit: SiteVisit;
        onEdit: () => void;
        onDelete?: () => void;
    };

    let { siteVisit, onEdit, onDelete }: Props = $props();

    const deleteForm = useForm({});

    // Status colors matching SiteVisitStatus PHP enum
    const statusColors: Record<string, string> = {
        scheduled: 'blue',
        completed: 'green',
        cancelled: 'red',
        rescheduled: 'orange',
    };

    // Purpose labels and colors matching SiteVisitPurpose PHP enum
    const purposeConfig: Record<string, { label: string; color: string }> = {
        initial_survey: { label: 'Initial Survey', color: 'blue' },
        follow_up: { label: 'Follow Up', color: 'cyan' },
        inspection: { label: 'Inspection', color: 'purple' },
        safety_check: { label: 'Safety Check', color: 'yellow' },
        progress_review: { label: 'Progress Review', color: 'teal' },
        final_inspection: { label: 'Final Inspection', color: 'green' },
        weekly_inspection: { label: 'Weekly Inspection', color: 'indigo' },
    };

    // Status labels matching SiteVisitStatus PHP enum
    const statusLabels: Record<string, string> = {
        scheduled: 'Scheduled',
        completed: 'Completed',
        cancelled: 'Cancelled',
        rescheduled: 'Rescheduled',
    };

    function formatDateTime(dateString: string) {
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    function handleDelete() {
        if (confirm('Are you sure you want to delete this site visit?')) {
            deleteForm.delete(`/web-api/SiteVisit/${siteVisit.id}`, {
                onSuccess: () => {
                    toast.success('Site visit deleted successfully');
                    if (onDelete) onDelete();
                },
                onError: () => {
                    toast.error('Failed to delete site visit');
                }
            });
        }
    }

    function viewOnScheduler() {
        const displayDate = new Date(siteVisit.start_date_time);
        router.visit(route('schedular.index', {
            displayDate: displayDate.toISOString(),
            highlightedAssignmentId: siteVisit.id
        }));
    }
</script>

<Card.Root class="mb-4">
    <Card.Header>
        <div class="flex items-start justify-between">
            <div class="flex-1">
                <div class="flex items-center gap-2 mb-2">
                    <Card.Title class="text-lg">{siteVisit.title}</Card.Title>
                    <PicklistBadge
                        label={statusLabels[siteVisit.status] || siteVisit.status}
                        color={statusColors[siteVisit.status] || 'gray'}
                    />
                    {#if siteVisit.purpose}
                        <PicklistBadge
                            label={purposeConfig[siteVisit.purpose]?.label || siteVisit.purpose}
                            color={purposeConfig[siteVisit.purpose]?.color || 'gray'}
                        />
                    {/if}
                </div>
            </div>
            <div class="flex gap-2">
                <Button variant="outline" size="sm" onclick={viewOnScheduler} title="View on Scheduler">
                    <CalendarDays class="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onclick={onEdit}>
                    <Pencil class="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onclick={handleDelete}>
                    <Trash2 class="h-4 w-4 text-red-600" />
                </Button>
            </div>
        </div>
    </Card.Header>
    <Card.Content>
        <div class="space-y-3">
            <!-- Date & Time -->
            <div class="flex items-start gap-2 text-sm">
                <Calendar class="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                    <div class="font-medium">Start: {formatDateTime(siteVisit.start_date_time)}</div>
                    <div class="text-muted-foreground">End: {formatDateTime(siteVisit.end_date_time)}</div>
                </div>
            </div>

            <!-- Assigned Employees -->
            {#if siteVisit.employee_assignments && siteVisit.employee_assignments.length > 0}
                <div class="flex items-start gap-2 text-sm">
                    <Users class="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                        <div class="font-medium">Assigned Employees:</div>
                        <div class="flex flex-wrap gap-2 mt-1">
                            {#each siteVisit.employee_assignments as assignment (assignment.id)}
                                {#if assignment.employee?.id}
                                    <a href={route('employees.show', { employee: assignment.employee.id })}>
                                        <Badge variant="secondary" class="hover:bg-secondary/80 cursor-pointer transition-colors">
                                            {assignment.employee.first_name} {assignment.employee.last_name}
                                        </Badge>
                                    </a>
                                {:else}
                                    <Badge variant="secondary">
                                        {assignment.employee?.first_name} {assignment.employee?.last_name}
                                    </Badge>
                                {/if}
                            {/each}
                        </div>
                    </div>
                </div>
            {/if}

            <!-- Assigned Vehicle -->
            {#if siteVisit.vehicle}
                <div class="flex items-start gap-2 text-sm">
                    <Car class="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                        <div class="font-medium">Assigned Vehicle:</div>
                        <div class="text-muted-foreground">
                            {siteVisit.vehicle.make} {siteVisit.vehicle.model} - {siteVisit.vehicle.registration}
                        </div>
                    </div>
                </div>
            {/if}

            <!-- Notes -->
            {#if siteVisit.notes}
                <div class="text-sm">
                    <div class="font-medium mb-1">Notes:</div>
                    <div class="text-muted-foreground whitespace-pre-wrap">{siteVisit.notes}</div>
                </div>
            {/if}

            <!-- Findings -->
            {#if siteVisit.findings}
                <div class="text-sm">
                    <div class="font-medium mb-1">Findings:</div>
                    <div class="text-muted-foreground whitespace-pre-wrap">{siteVisit.findings}</div>
                </div>
            {/if}
        </div>
    </Card.Content>
</Card.Root>
